// src/components/webinar_funnel/sections/FilterControls.jsx
import React from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

const DATE_OPTIONS = [
  { id: '7days',  label: 'Last 7 days' },
  { id: '30days', label: 'Last 30 days' },
  { id: '90days', label: 'Last 90 days' },
  { id: 'custom', label: 'Custom range' },
];

const FilterControls = ({
  filterType, setFilterType,
  dateRange, setDateRange,
  selectedWebinars, setSelectedWebinars,
  customStartDate, setCustomStartDate,
  customEndDate, setCustomEndDate,
  showFilterDropdown, setShowFilterDropdown,
  data,
}) => {
  const activeCount =
    filterType === 'dateRange' && dateRange !== 'all' ? 1
    : filterType === 'webinars' ? selectedWebinars.size
    : 0;

  const clearAll = () => {
    setFilterType('all');
    setDateRange('all');
    setSelectedWebinars(new Set());
    setCustomStartDate('');
    setCustomEndDate('');
    setShowFilterDropdown(false);
  };

  const toggleWebinar = (id) => {
    const next = new Set(selectedWebinars);
    next.has(id.toString()) ? next.delete(id.toString()) : next.add(id.toString());
    setSelectedWebinars(next);
  };

  return (
    <div className="bg-darkblue-50 border border-darkblue-100 rounded-xl px-4 py-3 mb-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Label */}
        <span className="text-xs font-bold text-darkblue-500 flex-shrink-0">Filter:</span>

        {/* All data */}
        <button
          onClick={clearAll}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            filterType === 'all'
              ? 'bg-darkblue-500 text-white border-darkblue-500'
              : 'bg-white text-darkblue-500 border-grey-300 hover:border-darkblue-200'
          }`}
        >
          All data
        </button>

        {/* Date range buttons */}
        {DATE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => {
              setFilterType('dateRange');
              setDateRange(opt.id);
              setSelectedWebinars(new Set());
              if (opt.id !== 'custom') setShowFilterDropdown(false);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filterType === 'dateRange' && dateRange === opt.id
                ? 'bg-darkblue-500 text-white border-darkblue-500'
                : 'bg-white text-darkblue-500 border-grey-300 hover:border-darkblue-200'
            }`}
          >
            {opt.label}
          </button>
        ))}

        {/* Divider */}
        <div className="w-px h-4 bg-grey-300 flex-shrink-0" />

        {/* Select webinars */}
        <div className="relative">
          <button
            onClick={() => {
              setFilterType('webinars');
              setShowFilterDropdown(!showFilterDropdown);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filterType === 'webinars' && selectedWebinars.size > 0
                ? 'bg-darkblue-500 text-white border-darkblue-500'
                : 'bg-white text-darkblue-500 border-grey-300 hover:border-darkblue-200'
            }`}
          >
            {selectedWebinars.size > 0
              ? `${selectedWebinars.size} webinar${selectedWebinars.size !== 1 ? 's' : ''}`
              : 'Select webinars'}
            <ChevronDownIcon className="w-3 h-3" />
          </button>

          {/* Webinar picker dropdown */}
          {showFilterDropdown && filterType === 'webinars' && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setShowFilterDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-grey-200 rounded-xl shadow-lg z-30 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-grey-200 bg-grey-50 flex items-center justify-between">
                  <span className="text-xs font-semibold text-darkblue-500">Select webinars</span>
                  <button
                    onClick={() => setSelectedWebinars(new Set())}
                    className="text-xs text-brightblue-600 hover:text-brightblue-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto p-2 space-y-0.5">
                  {data.map((item) => (
                    <label
                      key={item.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-grey-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedWebinars.has(item.id.toString())}
                        onChange={() => toggleWebinar(item.id)}
                        className="rounded border-grey-300 text-brightblue-500 focus:ring-brightblue-300"
                      />
                      <span className="text-xs text-darkblue-500">
                        Campaign {item.id}
                        <span className="text-grey-400 ml-1">
                          ({new Date(item.date).toLocaleDateString()})
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="px-4 py-2.5 border-t border-grey-200 bg-grey-50">
                  <button
                    onClick={() => setShowFilterDropdown(false)}
                    className="w-full px-3 py-1.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-xs font-semibold transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Custom date inputs (inline when active) */}
        {filterType === 'dateRange' && dateRange === 'custom' && (
          <div className="flex items-center gap-2 ml-1">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-2 py-1 border border-grey-300 rounded-lg text-xs text-darkblue-500 focus:outline-none focus:ring-2 focus:ring-brightblue-300"
            />
            <span className="text-xs text-grey-400">to</span>
            <input
              type="date"
              value={customEndDate}
              min={customStartDate || undefined}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-2 py-1 border border-grey-300 rounded-lg text-xs text-darkblue-500 focus:outline-none focus:ring-2 focus:ring-brightblue-300"
            />
          </div>
        )}

        {/* Active count badge + clear */}
        {activeCount > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-600 border border-brand-200">
              {activeCount} filter{activeCount !== 1 ? 's' : ''} active
            </span>
            <button
              onClick={clearAll}
              className="p-1 rounded-lg text-grey-400 hover:text-pink-600 hover:bg-pink-50 transition-colors"
              title="Clear filters"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Campaign count */}
        {activeCount === 0 && (
          <span className="ml-auto text-xs font-semibold text-grey-400">
            {data.length} campaigns
          </span>
        )}
      </div>
    </div>
  );
};

export default FilterControls;